import { useRouter } from 'next/router'
import { useCallback, useMemo, useState } from 'react'

import { toaster } from '@/components/Toaster'
import { TaskDefinition, TaskFilters, useTasksBoardAll } from '@/feature/execution-board'
import { useTripUpdates } from '@/feature/execution-board/hooks/useTripUpdates'
import { ReportFormat } from '@/feature/reports/types/Report'

import { defaultOptions, PaginationPayload } from '@/lib/core/components/Pagination'
import { routePaths } from '@/lib/core/constants/routes'
import { ListFilters, SortDirection, SortInfo } from '@/lib/core/interfaces/common.types'

import { checkIsDateAndConvertToBackendStandard } from '@/lib/utils/date-time'

import { sanitizeSearchParam } from '../../utils/sanitizeSearchParam'
import { useExportTasksRequest } from '../../api/useExportTasks'

const defaultFilters: ListFilters = {
  page: 1,
  pageSize: defaultOptions[1].value,
}

export const useExecutionBoard = () => {
  const router = useRouter()
  const initialSearch = sanitizeSearchParam(router.query.search)
  const [filters, setFilters] = useState<TaskFilters>({ ...defaultFilters, search: initialSearch })
  const { data, isLoading, mutate: refreshTasks } = useTasksBoardAll(filters)
  const [createTaskModalOpen, setCreateTaskModalOpen] = useState(false)
  const [importTaskModalOpen, setImportTaskModalOpen] = useState(false)

  const taskIds = useMemo(() => data.results?.map((task) => task.taskDefinition.id), [data.results])
  const tripProgress = useTripUpdates(taskIds)

  const [openDeleteModal, setOpenDeleteModal] = useState(false)
  const [selectedToDelete, setSelectedToDelete] = useState<TaskDefinition | null>(null)

  // The task whose focus modal is open. Local state on purpose: this used to set focusedTaskId on
  // the Observatory's persistent store and navigate to /map, which rewrote the URL's `page-state`
  // and left focus state behind on the Observatory. The modal reviews the task in place instead.
  const [focusedTask, setFocusedTask] = useState<TaskDefinition | null>(null)

  const handleViewTask = useCallback((task: TaskDefinition) => {
    setFocusedTask(task)
  }, [])

  const closeFocusModal = useCallback(() => {
    setFocusedTask(null)
  }, [])

  const handleCreateTask = useCallback(() => {
    setCreateTaskModalOpen(true)
  }, [])

  const handleImportTasks = useCallback(() => {
    setImportTaskModalOpen(true)
  }, [])

  const { trigger: enqueueReport } = useExportTasksRequest()

  const handleExportTasks = async () => {
    try {
      enqueueReport({
        fromDate: filters.dateFrom ? new Date(filters.dateFrom) : undefined,
        toDate: filters.dateTo ? new Date(filters.dateTo) : undefined,
        status: filters.status,
        orgFrom: filters.orgFrom,
        orgTo: filters.orgTo,
        outputFormat: ReportFormat.CSV,
      })
      router.push(`${routePaths.reportOverview}/downloads`, undefined, { shallow: true })
    } catch {
      toaster({
        type: 'error',
        message: 'Failed to export tasks. Please try again.',
      })
    }
  }

  const onPaginationChange = useCallback((paginationFilters: PaginationPayload) => {
    setFilters((prevFilters) => ({
      ...prevFilters,
      ...paginationFilters,
    }))
  }, [])

  const onChangeFilter = useCallback((filters: TaskFilters) => {
    setFilters((prevFilters) => ({
      ...prevFilters,
      ...filters,
    }))
  }, [])

  const onSearch = useCallback((search?: string) => {
    setFilters((prevFilters) => {
      if (search) {
        const convertedDate = checkIsDateAndConvertToBackendStandard(search)
        if (convertedDate) {
          return {
            ...prevFilters,
            page: 1,
            search: convertedDate,
          }
        }
      }
      return {
        ...prevFilters,
        page: 1,
        search,
      }
    })
  }, [])

  const clearSearch = useCallback(() => {
    setFilters((prevFilters) => ({
      ...prevFilters,
      page: 1,
      search: undefined,
    }))
    router.replace('/execution-board/all', undefined, { shallow: true })
  }, [router])

  const onSort = useCallback((key: string, direction: SortDirection | undefined) => {
    const newSort: SortInfo = direction ? { [key]: direction } : {}
    setFilters((prev) => ({
      ...prev,
      ordering: newSort,
    }))
  }, [])

  const onDelete = useCallback((task: TaskDefinition) => {
    setSelectedToDelete(task)
    setOpenDeleteModal(true)
  }, [])

  const closeDeleteModal = useCallback(() => {
    setOpenDeleteModal(false)
  }, [])

  const onTaskDeleted = useCallback(() => {
    refreshTasks()
    setSelectedToDelete(null)
    toaster({
      type: 'success',
      message: 'Task deleted successfully',
    })
  }, [refreshTasks])

  const closeCreateModal = useCallback(() => {
    setCreateTaskModalOpen(false)
  }, [])

  const closeImportModal = useCallback(() => {
    setImportTaskModalOpen(false)
  }, [])

  return {
    data,
    isLoading,
    filters,
    tripProgress,
    refreshTasks,
    createTaskModalOpen,
    importTaskModalOpen,
    openDeleteModal,
    selectedToDelete,
    focusedTask,
    handleViewTask,
    closeFocusModal,
    handleCreateTask,
    handleImportTasks,
    handleExportTasks,
    onPaginationChange,
    onChangeFilter,
    onSearch,
    clearSearch,
    onSort,
    onDelete,
    closeDeleteModal,
    onTaskDeleted,
    closeCreateModal,
    closeImportModal,
  }
}
